/**
 * /settings → Export / Import.
 *
 * Geeft elke workspace-member de mogelijkheid om:
 *   - **Export**: een JSON-bestand te downloaden met alle live
 *     concepten, relaties en diagrammen in de huidige branch.
 *   - **Import**: zo'n JSON-bestand te uploaden naar de huidige
 *     workspace + branch. Nieuwe ids worden gegenereerd en de
 *     uploadende user wordt owner van alles.
 *
 * Use-cases:
 *   - Lokale dev → productie kopie van een model
 *   - Tussen workspaces (bv. een template-workspace klonen)
 *   - Backup vóór een grote refactor (alternatief voor snapshots)
 *
 * Open Group AMEFF (XML) export staat op de roadmap (zie
 * `docs/architecture/05-backlog.md`).
 */
import i18n from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type ImportResultDTO, type WorkPackageDTO } from "../api/client";
import { ImportingModal } from "../editor/ImportingModal";
import { announceThenWait, finishOperation } from "../state/announceFirst";
import { useSession } from "../state/session";
import { useWorkspaceData } from "../state/workspace";
import { AmeffLabelReview } from "./AmeffLabelReview";

interface AmeffLabel {
  concept_id: string;
  name: string;
  type_id: string;
  stereotypes: string[];
  source: "heuristic" | "unclassified";
}

interface AmeffDiagram {
  diagram_id: string;
  name: string;
  view_kind: string;
  source: "heuristic";
}

interface AmeffAmbiguous {
  concept_id: string;
  name: string;
  type_id: string;
  stereotype: string;
  legacy_type_id: string;
  candidates: string[];
}

type ExportScope = "workspace" | "workpackage";

export function PortabilitySettings() {
  const { t } = useTranslation();
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const ameffFileRef = useRef<HTMLInputElement | null>(null);

  const [busy, setBusy] = useState(false);
  // Separate van `busy` zodat de ImportingModal alleen tijdens IMPORT
  // verschijnt, niet ook bij export (waar `busy` ook true wordt).
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  /** Resultaat van de laatste import. Houden we vast zodat we de
   *  `[imported]`-suffix-lijst kunnen tonen voor review. */
  const [lastImport, setLastImport] = useState<ImportResultDTO | null>(null);
  /** AMEFF-Label-classificatie review (M6.5-W8c). Wanneer niet-leeg
   *  toont een modal de gebruiker per Label de heuristic-suggestie +
   *  een dropdown om te bevestigen / wijzigen / terug naar Tekening. */
  const [ameffLabels, setAmeffLabels] = useState<AmeffLabel[]>([]);
  const [ameffDiagrams, setAmeffDiagrams] = useState<AmeffDiagram[]>([]);
  const [ameffAmbiguous, setAmeffAmbiguous] = useState<AmeffAmbiguous[]>([]);
  const [ameffTargetBranchId, setAmeffTargetBranchId] = useState<string | null>(null);

  // M6.5-W8a — export-scope-keuze
  const [scope, setScope] = useState<ExportScope>("workspace");
  const [workpackages, setWorkpackages] = useState<WorkPackageDTO[]>([]);
  const [selectedWpId, setSelectedWpId] = useState<string>("");
  // Doel-WP voor import — default = de WP waarin de gebruiker nu actief
  // is, zodat de bestaande "importeer in huidige editor-WP"-flow blijft
  // werken zonder extra klikken.
  const sessionWpId = useSession((s) => s.workpackageId);
  const [importWpId, setImportWpId] = useState<string>("");

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    api
      .listWorkpackages(workspaceId)
      .then((r) => {
        if (cancelled) return;
        setWorkpackages(r.workpackages);
        if (r.workpackages[0]) setSelectedWpId(r.workpackages[0].id);
        // Default import-target = huidige editor-WP, anders eerste WP
        const defaultImport =
          (sessionWpId && r.workpackages.find((wp) => wp.id === sessionWpId)?.id) ??
          r.workpackages[0]?.id ??
          "";
        setImportWpId(defaultImport);
      })
      .catch(() => {
        // Stilletjes negeren — WP-scope is optioneel.
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, sessionWpId]);

  const onExport = async () => {
    if (!workspaceId || !branchId) return;
    if (scope === "workpackage" && !selectedWpId) {
      setError(t("wpSettings.noWorkpackage"));
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const opts = scope === "workpackage" ? { workpackageId: selectedWpId } : undefined;
      const { blob, filename } = await api.exportWorkspace(workspaceId, branchId, opts);
      // Trigger download via een dynamische <a>.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Tel inhoud via client-side parse — Blob blijft leesbaar na
      // createObjectURL, dus geen tweede fetch nodig.
      // Drawing-types (drawing.Rectangle, drawing.Line, etc.) zijn
      // visuele decoratie op diagrammen — ze tellen we apart zodat de
      // gebruiker ziet welke 'echte' content er in de export zit.
      let countsLine = "";
      try {
        const parsed = JSON.parse(await blob.text()) as {
          concepts?: Array<{ typeId?: string }>;
          relations?: Array<{ typeId?: string }>;
          diagrams?: unknown[];
          custom_view_kinds?: unknown[];
        };
        const allConcepts = parsed.concepts ?? [];
        const allRelations = parsed.relations ?? [];
        const drawingConcepts = allConcepts.filter((cn) =>
          cn.typeId?.startsWith("drawing."),
        ).length;
        const drawingRelations = allRelations.filter((r) =>
          r.typeId?.startsWith("drawing."),
        ).length;
        const realConcepts = allConcepts.length - drawingConcepts;
        const realRelations = allRelations.length - drawingRelations;
        const diagramN = parsed.diagrams?.length ?? 0;
        const cvkN = parsed.custom_view_kinds?.length ?? 0;
        const parts: string[] = [];
        if (realConcepts > 0) {
          parts.push(t("settings.portability.countConcepts", { count: realConcepts }));
        }
        if (realRelations > 0) {
          parts.push(t("settings.portability.countRelations", { count: realRelations }));
        }
        if (diagramN > 0) {
          parts.push(t("settings.portability.countDiagrams", { count: diagramN }));
        }
        if (cvkN > 0) {
          parts.push(t("settings.portability.countCustomViewKinds", { count: cvkN }));
        }
        const drawingTotal = drawingConcepts + drawingRelations;
        if (drawingTotal > 0) {
          parts.push(t("settings.portability.countDrawings", { count: drawingTotal }));
        }
        if (parts.length > 0) countsLine = ` — ${parts.join(", ")}`;
      } catch {
        // Parse-fail = informatieve telling skippen; download is hoofdzaak.
      }
      setInfo(
        t("settings.portability.exportSuccessWithCounts", {
          filename,
          size: formatBytes(blob.size),
          counts: countsLine,
        }),
      );
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  /** M6.5-W8d — AMEFF (XML) export. Volgt dezelfde scope-keuze als de
   *  JSON-export: bij "workpackage" gebruiken we de mainBranchId van de
   *  geselecteerde WP, anders de huidige session-branch. */
  const onExportAmeff = async () => {
    if (!workspaceId || !branchId) return;
    if (scope === "workpackage" && !selectedWpId) {
      setError(t("wpSettings.noWorkpackage"));
      return;
    }
    const targetBranchId =
      scope === "workpackage"
        ? (workpackages.find((wp) => wp.id === selectedWpId)?.mainBranchId ?? branchId)
        : branchId;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const { blob, filename } = await api.exportAmeff(workspaceId, targetBranchId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setInfo(t("settings.portability.exportSuccess", { filename, size: formatBytes(blob.size) }));
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const onPickImportFile = () => {
    fileRef.current?.click();
  };

  const onPickImportAmeffFile = () => {
    ameffFileRef.current?.click();
  };

  const onImportAmeffFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !workspaceId) return;
    const targetWp = workpackages.find((wp) => wp.id === importWpId);
    const targetBranchId = targetWp?.mainBranchId ?? branchId;
    if (!targetBranchId) {
      setError(t("settings.portability.errorNoTargetBranch"));
      return;
    }
    const targetLabel = targetWp
      ? `'${targetWp.name}'${targetWp.isBase ? " (Base)" : ""}`
      : t("settings.portability.currentWp");
    if (
      !confirm(
        t("settings.portability.ameffImportConfirm", {
          filename: file.name,
          size: formatBytes(file.size),
          target: targetLabel,
        }),
      )
    ) {
      return;
    }
    setBusy(true);
    setImporting(true);
    setError(null);
    setInfo(null);
    try {
      const xml = await file.text();
      const uitkomst = await announceThenWait(workspaceId, targetBranchId, {
        kind: "import",
        label: file.name,
      });
      if (!uitkomst.go) {
        setInfo(t("settings.portability.importCancelled"));
        return;
      }
      let r: Awaited<ReturnType<typeof api.importAmeff>>;
      try {
        r = await api.importAmeff(workspaceId, targetBranchId, xml);
      } finally {
        await finishOperation(workspaceId, targetBranchId, uitkomst.operationId);
      }
      const parts: string[] = [];
      if (r.concepts_inserted > 0) {
        parts.push(t("settings.portability.ameffConcepts", { n: r.concepts_inserted }));
      }
      if (r.relations_inserted > 0) {
        parts.push(t("settings.portability.ameffRelations", { n: r.relations_inserted }));
      }
      if (r.diagrams_inserted > 0) {
        parts.push(t("settings.portability.ameffDiagrams", { n: r.diagrams_inserted }));
      }
      const skipNotes: string[] = [];
      if (r.concepts_skipped.length > 0)
        skipNotes.push(
          t("settings.portability.ameffConceptsSkipped", { n: r.concepts_skipped.length }),
        );
      if (r.relations_skipped.length > 0)
        skipNotes.push(
          t("settings.portability.ameffRelationsSkipped", { n: r.relations_skipped.length }),
        );
      if (r.diagrams_skipped.length > 0)
        skipNotes.push(
          t("settings.portability.ameffDiagramsSkipped", { n: r.diagrams_skipped.length }),
        );
      if (r.relations_needing_attention && r.relations_needing_attention.length > 0)
        skipNotes.push(
          t("settings.portability.ameffRelationsToCheck", {
            n: r.relations_needing_attention.length,
          }),
        );
      const unknownNotes: string[] = [];
      if (r.unknown_element_types.length > 0)
        unknownNotes.push(
          t("settings.portability.unknownElementTypes", {
            types: r.unknown_element_types.join(", "),
          }),
        );
      if (r.unknown_relationship_types.length > 0)
        unknownNotes.push(
          t("settings.portability.unknownRelationshipTypes", {
            types: r.unknown_relationship_types.join(", "),
          }),
        );
      const tail = [...skipNotes, ...unknownNotes].join("; ");
      setInfo(
        t("settings.portability.ameffImportSuccess", {
          target: targetLabel,
          parts: parts.join(", ") || t("settings.portability.zeroItems"),
          tail: tail ? ` — ${tail}` : "",
        }),
      );
      if (branchId) await useWorkspaceData.getState().hydrate(workspaceId, branchId);
      // Foldertree + WP-selector luisteren naar dit event en refreshen
      // hun lokale state (folders + concepten in Base). Zonder dit
      // blijven nieuw-aangemaakte folders/objecten onzichtbaar tot
      // een handmatige hard refresh.
      window.dispatchEvent(new CustomEvent("ae:workpackages-changed"));
      // Trigger de Label-review-modal wanneer de import één of meer
      // Labels heeft geclassificeerd (heuristic) of als fallback
      // gelaten. Geen modal voor imports zonder Labels (puur typed-
      // element-XML).
      const hasLabels = r.labels && r.labels.length > 0;
      const hasDiagrams = r.diagrams && r.diagrams.length > 0;
      if (hasLabels || hasDiagrams) {
        setAmeffLabels(r.labels ?? []);
        setAmeffDiagrams(r.diagrams ?? []);
        setAmeffAmbiguous(r.ambiguous ?? []);
        setAmeffTargetBranchId(targetBranchId);
      }
    } catch (err) {
      setError(explain(err));
      // Refresh OOK na fail — partial-writes kunnen de DB in tussen-
      // state zetten; zonder hydrate blijft de UI op pre-import state
      // hangen terwijl D1 al data heeft.
      if (branchId) {
        await useWorkspaceData
          .getState()
          .hydrate(workspaceId, branchId)
          .catch(() => {});
      }
    } finally {
      setBusy(false);
      setImporting(false);
    }
  };

  const onImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // reset zodat dezelfde file opnieuw kan
    if (!file || !workspaceId) return;
    const targetWp = workpackages.find((wp) => wp.id === importWpId);
    const targetBranchId = targetWp?.mainBranchId ?? branchId;
    if (!targetBranchId) {
      setError(t("settings.portability.errorNoTargetBranch"));
      return;
    }
    const targetLabel = targetWp
      ? `'${targetWp.name}'${targetWp.isBase ? " (Base)" : ""}`
      : t("settings.portability.currentWp");
    if (
      !confirm(
        t("settings.portability.importConfirmTarget", {
          filename: file.name,
          size: formatBytes(file.size),
          target: targetLabel,
        }),
      )
    )
      return;
    setBusy(true);
    setImporting(true);
    setError(null);
    setInfo(null);
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error(t("settings.portability.importInvalidJson"));
      }
      const uitkomstJson = await announceThenWait(workspaceId, targetBranchId, {
        kind: "import",
        label: file.name,
      });
      if (!uitkomstJson.go) {
        setInfo(t("settings.portability.importCancelled"));
        return;
      }
      let result: Awaited<ReturnType<typeof api.importWorkspace>>;
      try {
        result = await api.importWorkspace(workspaceId, targetBranchId, parsed);
      } finally {
        await finishOperation(workspaceId, targetBranchId, uitkomstJson.operationId);
      }
      setLastImport(result);
      // Tel drawing-primitieven in de source-payload zodat we ze apart
      // van "echte" concepten/relaties kunnen melden (zelfde split als
      // de export-info-regel doet). Result-counters splitsen dit niet
      // expliciet — wel het totaal verwerkt; de drawing-count is dus
      // een schatting van "hoeveel van het totaal decoratie was".
      let drawingConceptN = 0;
      let drawingRelationN = 0;
      if (parsed && typeof parsed === "object") {
        const p = parsed as {
          concepts?: Array<{ typeId?: string }>;
          relations?: Array<{ typeId?: string }>;
        };
        drawingConceptN = (p.concepts ?? []).filter((cn) =>
          cn.typeId?.startsWith("drawing."),
        ).length;
        drawingRelationN = (p.relations ?? []).filter((r) =>
          r.typeId?.startsWith("drawing."),
        ).length;
      }
      const parts: string[] = [];
      const conceptTotal =
        result.concepts_inserted + result.concepts_updated + result.concepts_reused;
      const relationTotal = result.relations_inserted + result.relations_updated;
      const diagramTotal = result.diagrams_inserted + result.diagrams_updated;
      const cvkTotal = result.custom_view_kinds_inserted + result.custom_view_kinds_updated;
      const realConceptTotal = conceptTotal - drawingConceptN;
      const realRelationTotal = relationTotal - drawingRelationN;
      if (realConceptTotal > 0) {
        const conceptCounts = {
          total: realConceptTotal,
          inserted: result.concepts_inserted - drawingConceptN,
          updated: result.concepts_updated,
        };
        parts.push(
          result.concepts_reused > 0
            ? t("settings.portability.importConceptsPartReused", {
                ...conceptCounts,
                reused: result.concepts_reused,
              })
            : t("settings.portability.importConceptsPart", conceptCounts),
        );
      }
      if (realRelationTotal > 0) {
        parts.push(
          t("settings.portability.importRelationsPart", {
            total: realRelationTotal,
            inserted: result.relations_inserted - drawingRelationN,
            updated: result.relations_updated,
          }),
        );
      }
      if (diagramTotal > 0) {
        parts.push(
          t("settings.portability.importDiagramsPart", {
            total: diagramTotal,
            inserted: result.diagrams_inserted,
            updated: result.diagrams_updated,
          }),
        );
      }
      if (cvkTotal > 0) {
        parts.push(t("settings.portability.importCustomViewKindsPart", { total: cvkTotal }));
      }
      if (result.folders_created > 0) {
        parts.push(t("settings.portability.foldersCreated", { n: result.folders_created }));
      }
      const drawingTotal = drawingConceptN + drawingRelationN;
      if (drawingTotal > 0) {
        parts.push(t("settings.portability.countDrawings", { count: drawingTotal }));
      }
      if (result.relations_skipped > 0) {
        parts.push(
          t("settings.portability.importSkippedRelations", { count: result.relations_skipped }),
        );
      }
      const safetyNote = result.safety_snapshot_label
        ? t("settings.portability.importSafetyNote", { label: result.safety_snapshot_label })
        : "";
      setInfo(
        t("settings.portability.importedSuccessTarget", {
          target: targetLabel,
          parts: parts.join(", "),
          safetyNote,
        }),
      );
      // Lokale store herhydrateren — anders zie je de nieuwe data niet
      // tot een volgende refresh. Hydrate altijd de huidige editor-branch;
      // als de gebruiker naar een andere WP importeerde dan waar hij nu
      // staat, ziet hij de import pas na WP-switch (= de juiste UX:
      // import landt in target-WP, niet stiekem in zijn huidige view).
      if (branchId) await useWorkspaceData.getState().hydrate(workspaceId, branchId);
      // Foldertree refresh (zie comment bij AMEFF-handler hierboven).
      window.dispatchEvent(new CustomEvent("ae:workpackages-changed"));
    } catch (err) {
      setError(explain(err));
      // Refresh OOK na fail (zie hydrate-comment op AMEFF-handler).
      if (branchId) {
        await useWorkspaceData
          .getState()
          .hydrate(workspaceId, branchId)
          .catch(() => {});
      }
    } finally {
      setBusy(false);
      setImporting(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <ImportingModal visible={importing} />
      <div className="rounded border border-(--ld-border) bg-(--ld-raised) px-3 py-2 text-(--ld-text) text-sm">
        <p className="mb-1">
          <strong>{t("settings.portability.introTitle")}</strong>
          {t("settings.portability.introBefore")}
        </p>
        <p className="text-(--ld-muted) text-xs">
          {t("settings.portability.introDetail1")}
          <strong>{t("settings.portability.introDetailNewIds")}</strong>
          {t("settings.portability.introDetailAnd")}
          <strong>{t("settings.portability.introDetailOwner")}</strong>
          {t("settings.portability.introDetailAfter")}
        </p>
      </div>

      <section className="rounded border border-(--ld-border) bg-(--ld-surface) px-4 py-3">
        <h3 className="mb-2 font-semibold text-(--ld-text) text-sm">
          {t("settings.portability.exportTitle")}
        </h3>
        <p className="mb-3 text-(--ld-muted) text-sm">
          {t("settings.portability.exportDescription")}
        </p>
        <fieldset className="mb-3 flex flex-col gap-2 text-sm">
          <legend className="sr-only">{t("settings.portability.scopeLegend")}</legend>
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="export-scope"
              value="workspace"
              checked={scope === "workspace"}
              onChange={() => setScope("workspace")}
              disabled={busy}
            />
            <span>{t("settings.portability.scopeWorkspace")}</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="export-scope"
              value="workpackage"
              checked={scope === "workpackage"}
              onChange={() => setScope("workpackage")}
              disabled={busy || workpackages.length === 0}
            />
            <span>{t("settings.portability.scopeWorkpackage")}</span>
            {scope === "workpackage" && (
              <select
                value={selectedWpId}
                onChange={(e) => setSelectedWpId(e.target.value)}
                disabled={busy || workpackages.length === 0}
                className="ml-2 rounded border border-(--ld-control) px-2 py-0.5 text-xs"
              >
                {workpackages.map((wp) => (
                  <option key={wp.id} value={wp.id}>
                    {wp.name}
                    {wp.isBase ? " (Base)" : ""}
                  </option>
                ))}
              </select>
            )}
          </label>
          {scope === "workpackage" && (
            <p className="text-(--ld-muted) text-xs">{t("settings.portability.wpScopeHint")}</p>
          )}
        </fieldset>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void onExport()}
            disabled={busy || !workspaceId || !branchId}
            className="rounded bg-(--ld-primary) px-3 py-1.5 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
          >
            {t("settings.portability.exportButton")}
          </button>
          <button
            type="button"
            onClick={() => void onExportAmeff()}
            disabled={busy || !workspaceId || !branchId}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1.5 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised) disabled:text-(--ld-disabled-ink) disabled:hover:bg-(--ld-surface)"
            title={t("settings.portability.ameffExportTitle")}
          >
            {t("settings.portability.ameffExportButton")}
          </button>
        </div>
      </section>

      <section className="rounded border border-(--ld-border) bg-(--ld-surface) px-4 py-3">
        <h3 className="mb-2 font-semibold text-(--ld-text) text-sm">
          {t("settings.portability.importTitle")}
        </h3>
        <p className="mb-3 text-(--ld-muted) text-sm">
          {t("settings.portability.importDescriptionTarget")}
        </p>
        <label className="mb-3 flex items-center gap-2 text-sm">
          <span className="text-(--ld-muted)">{t("settings.portability.targetWpLabel")}</span>
          <select
            value={importWpId}
            onChange={(e) => setImportWpId(e.target.value)}
            disabled={busy || workpackages.length === 0}
            className="rounded border border-(--ld-control) px-2 py-0.5 text-xs"
          >
            {workpackages.length === 0 && <option value="">{t("settings.common.loading")}</option>}
            {workpackages.map((wp) => (
              <option key={wp.id} value={wp.id}>
                {wp.name}
                {wp.isBase ? " (Base)" : ""}
              </option>
            ))}
          </select>
        </label>
        <p className="mb-3 text-(--ld-muted) text-xs">
          {t("settings.portability.folderMatchHint")}
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          onChange={(e) => void onImportFile(e)}
          className="hidden"
        />
        <input
          ref={ameffFileRef}
          type="file"
          accept=".archimate,.xml,application/xml,text/xml"
          onChange={(e) => void onImportAmeffFile(e)}
          className="hidden"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onPickImportFile}
            disabled={busy || !workspaceId || !importWpId}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1.5 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised) disabled:text-(--ld-disabled-ink) disabled:hover:bg-(--ld-surface)"
          >
            {t("settings.portability.importButton")}
          </button>
          <button
            type="button"
            onClick={onPickImportAmeffFile}
            disabled={busy || !workspaceId || !importWpId}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1.5 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised) disabled:text-(--ld-disabled-ink) disabled:hover:bg-(--ld-surface)"
            title={t("settings.portability.ameffImportTitle")}
          >
            {t("settings.portability.ameffImportButton")}
          </button>
        </div>
      </section>

      {error && (
        <p className="rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
          {error}
        </p>
      )}
      {info && (
        <p className="rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
          {info}
        </p>
      )}

      {lastImport && lastImport.imported_with_suffix.length > 0 && (
        <SuffixedReviewPanel entries={lastImport.imported_with_suffix} />
      )}

      {(ameffLabels.length > 0 || ameffDiagrams.length > 0 || ameffAmbiguous.length > 0) &&
        workspaceId &&
        ameffTargetBranchId && (
          <AmeffLabelReview
            labels={ameffLabels}
            diagrams={ameffDiagrams}
            ambiguous={ameffAmbiguous}
            workspaceId={workspaceId}
            branchId={ameffTargetBranchId}
            onClose={() => {
              setAmeffLabels([]);
              setAmeffDiagrams([]);
              setAmeffAmbiguous([]);
              setAmeffTargetBranchId(null);
            }}
          />
        )}
    </section>
  );
}

/**
 * Toont per-categorie de objecten die `[imported]` achter hun naam
 * hebben gekregen. De gebruiker kan zo nakijken of er duplicaten
 * zijn ontstaan en de naam corrigeren via het Eigenschappen-panel
 * (concepten/diagrammen) of via /settings → Diagramtypes (custom
 * view-kinds). Een merge-tool voor bestaande+nieuwe duplicaten staat
 * in de backlog.
 */
function SuffixedReviewPanel({ entries }: { entries: ImportResultDTO["imported_with_suffix"] }) {
  const { t } = useTranslation();
  // Twee soorten review-items, met heel verschillende implicaties:
  //  - name_collision: een gelijknamig record bestond al → de naam in
  //    de DB heeft nu `[imported]`-suffix. Admin moet kiezen: rename
  //    of mergen
  //  - duplicate_uuid_resolved: UUID was elders in workspace al in
  //    gebruik → verse UUID toegekend, NAAM ONGEWIJZIGD. Geen actie
  //    nodig tenzij admin wil onderzoeken waar de oude UUID nog wijst
  const nameCollisions = entries.filter((e) => (e.reason ?? "name_collision") === "name_collision");
  const uuidResolved = entries.filter((e) => e.reason === "duplicate_uuid_resolved");

  return (
    <section className="flex flex-col gap-3">
      {nameCollisions.length > 0 && (
        <ReviewPanelGroup
          tone="amber"
          heading={t("settings.portability.suffixHeading", { count: nameCollisions.length })}
          body={t("settings.portability.suffixBody")}
          entries={nameCollisions}
          showSuffix
        />
      )}
      {uuidResolved.length > 0 && (
        <ReviewPanelGroup
          tone="slate"
          heading={t("settings.portability.uuidHeading", { count: uuidResolved.length })}
          body={t("settings.portability.uuidBody")}
          entries={uuidResolved}
          showSuffix={false}
        />
      )}
    </section>
  );
}

function ReviewPanelGroup({
  tone,
  heading,
  body,
  entries,
  showSuffix,
}: {
  tone: "amber" | "slate";
  heading: string;
  body: string;
  entries: ImportResultDTO["imported_with_suffix"];
  showSuffix: boolean;
}) {
  const { t } = useTranslation();
  const grouped = {
    concept: entries.filter((e) => e.kind === "concept"),
    relation: entries.filter((e) => e.kind === "relation"),
    diagram: entries.filter((e) => e.kind === "diagram"),
    custom_view_kind: entries.filter((e) => e.kind === "custom_view_kind"),
  };
  const labels: Record<keyof typeof grouped, string> = {
    concept: t("settings.portability.reviewLabelConcepts"),
    relation: t("settings.portability.reviewLabelRelations"),
    diagram: t("settings.portability.reviewLabelDiagrams"),
    custom_view_kind: t("settings.portability.reviewLabelCustomViewKinds"),
  };
  const borderCls =
    tone === "amber"
      ? "border-transparent bg-(--ld-notice-bg)"
      : "border-(--ld-control) bg-(--ld-raised)";
  const headingCls = tone === "amber" ? "text-(--ld-notice-ink)" : "text-(--ld-text)";
  const bodyCls = tone === "amber" ? "text-(--ld-notice-ink)" : "text-(--ld-text)";

  return (
    <section className={`rounded border ${borderCls} px-4 py-3 `}>
      <h3 className={`mb-1 font-semibold ${headingCls} text-sm`}>{heading}</h3>
      <p className={`mb-3 ${bodyCls} text-xs`}>{body}</p>
      <div className="flex flex-col gap-2">
        {(Object.keys(grouped) as Array<keyof typeof grouped>).map((kind) => {
          const items = grouped[kind];
          if (items.length === 0) return null;
          return (
            <details key={kind} className="rounded bg-(--ld-surface) px-3 py-2">
              <summary className={`cursor-pointer font-medium ${headingCls} text-sm`}>
                {labels[kind]} ({items.length})
              </summary>
              <ul className="mt-2 flex flex-col gap-1 text-xs">
                {items.map((item) => (
                  <li key={item.id} className="flex items-center gap-2">
                    <span className="font-mono text-(--ld-muted)">{item.id.slice(0, 8)}</span>
                    <span className="text-(--ld-text)">
                      {item.original_name}
                      {showSuffix ? " [imported]" : ""}
                    </span>
                    {item.type_id && (
                      <span className="rounded bg-(--ld-active) px-1 text-(--ld-muted)">
                        {item.type_id}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          );
        })}
      </div>
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return i18n.t("settings.common.unknownError");
}
